import { useState } from 'react'
import { useTelegram } from '@/shared/lib/telegram'
import { env } from '@/shared/config/env'
import { ENABLE_MOCK_AUTH, MOCK_INIT_DATA } from '@/shared/config/dev-mocks'

interface UploadResult {
  path: string
  url: string
}

interface UploadResponse {
  success: boolean
  data: UploadResult
  error?: string
}

/**
 * Получает актуальный initData из Telegram WebApp
 */
function getActualInitData(): string {
  if (ENABLE_MOCK_AUTH) {
    return MOCK_INIT_DATA
  }
  
  if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp?.initData) {
    return (window as any).Telegram.WebApp.initData
  }
  
  return ''
}

/**
 * Хук для загрузки файлов пользователя
 */
export function useUpload() {
  const { initData } = useTelegram()
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Загрузить файл на сервер
   * @param file - Файл для загрузки
   * @param folder - Папка назначения (по умолчанию 'diary')
   * @returns URL загруженного файла или null при ошибке
   */
  const upload = async (file: File, folder: string = 'diary'): Promise<string | null> => {
    if (!initData) {
      setError('Требуется авторизация')
      return null
    }

    setIsUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('folder', folder)

      const authInitData = getActualInitData()
      const isVk = typeof window !== 'undefined' && (
        (window as any).Telegram?.WebApp?.isVk === true ||
        window.location.search.includes('vk_app_id')
      )

      const authHeaders: Record<string, string> = isVk
        ? { 'X-VK-Init-Data': authInitData }
        : { 'X-Telegram-Init-Data': authInitData }

      if (isVk) {
        const webApp = (window as any).Telegram?.WebApp
        const user = webApp?.initDataUnsafe?.user
        if (user) {
          authHeaders['X-VK-User-Info'] = encodeURIComponent(JSON.stringify({
            first_name: user.first_name,
            last_name: user.last_name,
            username: user.username,
            photo_url: user.photo_url,
          }))
        }
      }

      const response = await fetch(`${env.apiUrl}/api/user/upload`, {
        method: 'POST',
        headers: authHeaders,
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Ошибка загрузки' }))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const result: UploadResponse = await response.json()
      
      if (!result.success || !result.data?.url) {
        throw new Error(result.error || 'Не удалось получить URL файла')
      }

      return result.data.url
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки'
      setError(message)
      console.error('[Upload] Error:', err)
      return null
    } finally {
      setIsUploading(false)
    }
  }

  /**
   * Сжать и загрузить изображение
   * @param file - Изображение для загрузки
   * @param maxSize - Максимальный размер стороны (по умолчанию 1200px)
   * @param quality - Качество JPEG (0-1, по умолчанию 0.85)
   */
  const uploadImage = async (
    file: File, 
    maxSize: number = 1200, 
    quality: number = 0.85
  ): Promise<string | null> => {
    // Сжимаем изображение перед загрузкой
    const compressedFile = await compressImage(file, maxSize, quality)
    return upload(compressedFile, 'diary')
  }

  return {
    upload,
    uploadImage,
    isUploading,
    error,
    clearError: () => setError(null),
  }
}

/**
 * Сжатие изображения
 */
async function compressImage(
  file: File, 
  maxSize: number, 
  quality: number
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img

        // Масштабируем если нужно
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width
          width = maxSize
        } else if (height > maxSize) {
          width = (width * maxSize) / height
          height = maxSize
        }

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Canvas context not available'))
          return
        }

        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'))
              return
            }

            // Создаем новый File из blob
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            })

            resolve(compressedFile)
          },
          'image/jpeg',
          quality
        )
      }

      img.onerror = () => reject(new Error('Failed to load image'))
      img.src = e.target?.result as string
    }

    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

export default useUpload

